import React from 'react'
import { createPortal } from 'react-dom'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { NotificationRecipientItem } from '../../services/notifications'
import { Button } from '../ui/button'

export interface NotificationDetailModalProps {
  notification: NotificationRecipientItem
  senderInfo: { name: string; avatarUrl: string | null } | null
  loadingAvatar: boolean
  onClose: () => void
}

// Função para converter URLs em links clicáveis
const renderTextWithLinks = (text: string): React.ReactNode => {
  if (!text) return text

  const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+|[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.(com|org|net|edu|gov|br|io|co|me|dev|app)[^\s]*)/gi

  const parts: React.ReactNode[] = []
  let lastIndex = 0
  let match
  let keyCounter = 0

  urlRegex.lastIndex = 0

  while ((match = urlRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      const textBefore = text.substring(lastIndex, match.index)
      if (textBefore) parts.push(textBefore)
    }

    let url = match[0]
    let href = url
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      href = 'https://' + url
    }

    parts.push(
      <a
        key={`link-${keyCounter++}`}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline break-all"
        onClick={(e) => e.stopPropagation()}
      >
        {url}
      </a>
    )

    lastIndex = match.index + match[0].length
  }

  if (lastIndex < text.length) {
    const remainingText = text.substring(lastIndex)
    if (remainingText) parts.push(remainingText)
  }

  if (parts.length === 0) return text
  if (parts.length === 1 && typeof parts[0] === 'string') return parts[0]
  return <>{parts}</>
}

const formatTimeAgo = (dateString: string): string => {
  const date = new Date(dateString)
  const now = new Date()
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000)

  if (diffInSeconds < 60) return 'Agora'
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m atrás`
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h atrás`
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d atrás`
  return date.toLocaleDateString('pt-BR', { day: 'numeric', month: 'short' })
}

const getInitials = (text: string): string => {
  if (!text) return 'N'
  const words = text.trim().split(/\s+/)
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase()
  return text.substring(0, 2).toUpperCase()
}

const getAvatarColor = (text: string): string => {
  const colors = [
    'bg-purple-500', 'bg-blue-500', 'bg-green-500', 'bg-yellow-500',
    'bg-pink-500', 'bg-indigo-500', 'bg-red-500', 'bg-teal-500',
  ]
  let hash = 0
  for (let i = 0; i < text.length; i++) {
    hash = text.charCodeAt(i) + ((hash << 5) - hash)
  }
  return colors[Math.abs(hash) % colors.length]
}

/**
 * Modal único de "Ver mensagem completa" usado no Dashboard e no menu de Notificações (sidebar).
 * Renderizado em portal no body para ficar sempre por cima (z-[9999]).
 */
const NotificationDetailModal: React.FC<NotificationDetailModalProps> = ({
  notification,
  senderInfo,
  loadingAvatar,
  onClose,
}) => {
  const createdAt = notification.created_at || notification.notification?.created_at || ''

  const modalContent = (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-[900px] w-[70%] max-h-[90vh] overflow-y-auto focus:outline-none"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-10 bg-white dark:bg-gray-800">
          <div className="flex items-center space-x-3">
            <div
              className={`w-10 h-10 rounded-full ${
                loadingAvatar || !senderInfo
                  ? 'bg-gray-300 dark:bg-gray-600'
                  : senderInfo?.avatarUrl
                    ? ''
                    : getAvatarColor(senderInfo?.name || 'Usuário')
              } flex items-center justify-center text-white font-semibold text-sm overflow-hidden`}
            >
              {loadingAvatar || !senderInfo ? (
                <div className="w-full h-full flex items-center justify-center bg-gray-300 dark:bg-gray-600 animate-pulse" />
              ) : senderInfo?.avatarUrl ? (
                <img
                  src={senderInfo.avatarUrl}
                  alt={senderInfo.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                getInitials(senderInfo?.name || 'Usuário')
              )}
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {notification.notification?.title || 'Notificação'}
              </h2>
              {(senderInfo?.name || loadingAvatar) && (
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-0.5">
                  {loadingAvatar ? (
                    <span className="inline-block w-24 h-4 bg-gray-200 dark:bg-gray-600 rounded animate-pulse" />
                  ) : (
                    <>
                      Enviado por <span className="font-medium">{senderInfo?.name}</span>
                    </>
                  )}
                </p>
              )}
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                {createdAt ? formatTimeAgo(createdAt) : 'Data não disponível'}
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            aria-label="Fechar modal"
          >
            <XMarkIcon className="h-5 w-5" />
          </Button>
        </div>

        {/* Conteúdo */}
        <div className="p-6">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Mensagem
            </label>
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
              <div className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap break-words">
                {renderTextWithLinks(notification.notification?.body || 'Sem conteúdo')}
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <Button variant="ghost" onClick={onClose}>
              Fechar
            </Button>
          </div>
        </div>
      </div>
    </div>
  )

  return createPortal(modalContent, document.body)
}

export default NotificationDetailModal
